import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  TextInput,
  Stack,
  Text,
  Box,
  Group,
  UnstyledButton,
  Badge,
  Button,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Search, Check, Plus } from 'react-feather';
import type { Topic } from '../../../../types';
import { topicsApi } from '../../../../services/api/topics';

interface TopicSelectModalProps {
  opened: boolean;
  onClose: () => void;
  /**
   * Selection callback fired only on explicit Save (MED-851 AC6). The optional
   * second argument is the participant-facing display_name override when the
   * modal is shown with that field enabled (MED-832 AC12). Empty/whitespace
   * input is normalized to null. Callers that don't surface a display_name
   * input can ignore the second argument.
   */
  onSelect: (topic: Topic, displayName?: string | null) => void;
  currentTopicId?: string | null;
  /** MED-832: the existing per-(cohort, session_number) participant-facing override, if any. */
  currentDisplayName?: string | null;
  sessionNumber?: number;
  groupName?: string;
  /**
   * Whether to render the Participant-facing name input. Defaults to true so
   * AC12 is honored everywhere the modal is used; surfaces that don't yet have
   * a cohort_session_topics row to attach the override to can opt out.
   */
  showDisplayNameField?: boolean;
}

/**
 * Modal for selecting a topic for a session.
 *
 * MED-851: clicking a topic STAGES the selection (visual ✓ Selected pill) but
 * does not commit. An explicit Save button commits the staged topic + display
 * name; Cancel / outside click discards. The participant-facing name input
 * remains editable while a topic is staged so the CM can review both before
 * committing.
 */
export function TopicSelectModal({
  opened,
  onClose,
  onSelect,
  currentTopicId,
  currentDisplayName = null,
  sessionNumber,
  groupName,
  showDisplayNameField = true,
}: TopicSelectModalProps) {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Inline-create state
  const [adding, setAdding] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // MED-832 AC12: participant-facing display_name input. Pre-filled from existing override.
  const [displayName, setDisplayName] = useState<string>(currentDisplayName ?? '');

  // MED-851 AC4: the user's currently-staged topic (not yet committed).
  // null means "no different topic staged" — Save still commits display_name
  // changes against currentTopicId per AC9.
  const [stagedTopicId, setStagedTopicId] = useState<string | null>(null);

  const refreshTopics = async () => {
    setLoading(true);
    try {
      const list = await topicsApi.getAll();
      setTopics(list);
    } catch {
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  // Load topics when the modal opens
  useEffect(() => {
    if (opened) {
      refreshTopics();
      setDisplayName(currentDisplayName ?? '');
      setStagedTopicId(null);
    }
  }, [opened, currentDisplayName]);

  // Reset transient state when modal closes
  useEffect(() => {
    if (!opened) {
      setSearchQuery('');
      setAdding(false);
      setNewTopicName('');
      setCreateError(null);
      setStagedTopicId(null);
    }
  }, [opened]);

  // Filter topics by search query
  const filteredTopics = useMemo(() => {
    if (!searchQuery.trim()) return topics;
    const q = searchQuery.toLowerCase();
    return topics.filter(t => t.name.toLowerCase().includes(q));
  }, [topics, searchQuery]);

  // Separate current topic from the list
  const currentTopic = useMemo(() => {
    if (!currentTopicId) return null;
    return topics.find(t => t.id === currentTopicId) || null;
  }, [topics, currentTopicId]);

  const otherTopics = useMemo(() => {
    if (!currentTopicId) return filteredTopics;
    return filteredTopics.filter(t => t.id !== currentTopicId);
  }, [filteredTopics, currentTopicId]);

  // AC15 / MED-832 AC14: empty/whitespace display_name is treated as null.
  const normalizedDisplayName = displayName.trim() === '' ? null : displayName.trim();

  // MED-851 AC4: clicking a topic stages it; does NOT commit and does NOT close.
  const handleStage = (topic: Topic) => {
    setStagedTopicId(topic.id);
  };

  // MED-851 AC9: derive the topic that Save will commit. If a topic is staged,
  // use it; otherwise fall back to the current topic (for display-name-only saves).
  const effectiveTopicId = stagedTopicId ?? currentTopicId ?? null;
  const topicChanged = stagedTopicId !== null && stagedTopicId !== (currentTopicId ?? null);
  const displayNameChanged =
    (currentDisplayName ?? '').trim() !== displayName.trim();
  const canSave = effectiveTopicId !== null && (topicChanged || displayNameChanged);

  const handleSave = () => {
    if (!canSave || effectiveTopicId === null) return;
    const topicToCommit =
      topics.find(t => t.id === effectiveTopicId) ??
      (currentTopic && currentTopic.id === effectiveTopicId ? currentTopic : null);
    if (!topicToCommit) return;
    onSelect(topicToCommit, showDisplayNameField ? normalizedDisplayName : undefined);
    onClose();
  };

  // AC8: empty/whitespace names rejected at form level — no API call.
  const trimmedNewName = newTopicName.trim();
  const canSubmit = trimmedNewName.length > 0 && !creating;

  // MED-851 AC10: creating a custom topic stages it (does not commit, does not
  // close). The display_name field retains whatever the CM typed.
  const handleCreate = async () => {
    if (!canSubmit) return;
    setCreating(true);
    setCreateError(null);
    try {
      const result = await topicsApi.create(trimmedNewName);
      await refreshTopics();
      notifications.show({
        title: result.created ? 'Topic created' : 'Topic already exists — selected',
        message: result.topic.name,
        color: result.created ? 'medleyGreen' : 'gray',
      });
      setStagedTopicId(result.topic.id);
      setAdding(false);
      setNewTopicName('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create topic';
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  };

  // Build subtitle
  const subtitle = [sessionNumber && `Session ${sessionNumber}`, groupName]
    .filter(Boolean)
    .join(' • ');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={2}>
          <Text fw={600} size="lg">
            Select Topic
          </Text>
          {subtitle && (
            <Text size="sm" c="dimmed">
              {subtitle}
            </Text>
          )}
        </Stack>
      }
      size="md"
    >
      <Stack gap="md">
        {/* Search Input */}
        <TextInput
          placeholder="Search topics..."
          leftSection={<Search size={16} />}
          value={searchQuery}
          onChange={e => setSearchQuery(e.currentTarget.value)}
        />

        {/* MED-832 AC12: optional participant-facing label override */}
        {showDisplayNameField && (
          <TextInput
            label="Participant-facing name (optional)"
            description="If set, participants see this label instead of the canonical topic name. CMs and reporting always see the canonical name."
            placeholder="Leave blank to use the topic name"
            value={displayName}
            onChange={e => setDisplayName(e.currentTarget.value)}
            data-testid="topic-display-name-input"
          />
        )}

        {/* Topic List */}
        <Box
          style={{
            maxHeight: 400,
            overflowY: 'auto',
          }}
        >
          {loading ? (
            <Text size="sm" c="dimmed" ta="center" py="md">
              Loading topics...
            </Text>
          ) : (
            <Stack gap={0}>
              {/* Current Topic Section */}
              {currentTopic && !searchQuery && (
                <>
                  <Text size="xs" c="dimmed" fw={500} tt="uppercase" py="xs" px="sm">
                    Current Topic
                  </Text>
                  <TopicRow
                    topic={currentTopic}
                    pill={
                      // AC4 wireframe: CURRENT pill drops once a different topic is staged.
                      stagedTopicId === null || stagedTopicId === currentTopic.id
                        ? 'current'
                        : 'none'
                    }
                    onClick={() => handleStage(currentTopic)}
                  />
                </>
              )}

              {/* All Topics */}
              {currentTopic && !searchQuery && otherTopics.length > 0 && (
                <Text size="xs" c="dimmed" fw={500} tt="uppercase" py="xs" px="sm" mt="sm">
                  All Topics
                </Text>
              )}
              {otherTopics.map(topic => (
                <TopicRow
                  key={topic.id}
                  topic={topic}
                  pill={
                    // AC4: stagedTopicId !== currentTopicId implies a NEW topic is being staged.
                    stagedTopicId === topic.id && stagedTopicId !== (currentTopicId ?? null)
                      ? 'selected'
                      : 'none'
                  }
                  onClick={() => handleStage(topic)}
                />
              ))}

              {/* No Results — only when no items AND no add UI is open */}
              {!loading && filteredTopics.length === 0 && !adding && (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  No topics match your search
                </Text>
              )}
            </Stack>
          )}
        </Box>

        {/* AC3: "+ Add custom topic" entry as the last item / CTA */}
        {!adding ? (
          <UnstyledButton
            onClick={() => {
              setAdding(true);
              setNewTopicName(searchQuery.trim());
            }}
            py="sm"
            px="sm"
            style={{
              borderTop: '1px solid var(--mantine-color-gray-2)',
              borderRadius: 0,
            }}
            data-testid="add-custom-topic-trigger"
          >
            <Group gap="xs">
              <Plus size={14} />
              <Text size="sm" fw={500}>
                Add custom topic
              </Text>
            </Group>
          </UnstyledButton>
        ) : (
          // AC4: inline input for the new topic name + submit/cancel
          <Stack
            gap="xs"
            p="sm"
            style={{
              borderTop: '1px solid var(--mantine-color-gray-2)',
            }}
          >
            <TextInput
              label="New topic name"
              placeholder="e.g. Quarterly Strategy Review"
              value={newTopicName}
              onChange={e => setNewTopicName(e.currentTarget.value)}
              error={createError}
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter' && canSubmit) {
                  handleCreate();
                } else if (e.key === 'Escape') {
                  setAdding(false);
                  setNewTopicName('');
                  setCreateError(null);
                }
              }}
              disabled={creating}
              data-testid="add-custom-topic-input"
            />
            <Group justify="flex-end" gap="xs">
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                onClick={() => {
                  setAdding(false);
                  setNewTopicName('');
                  setCreateError(null);
                }}
                disabled={creating}
              >
                Cancel add
              </Button>
              <Button
                size="xs"
                color="medleyGreen"
                onClick={handleCreate}
                disabled={!canSubmit}
                loading={creating}
                data-testid="add-custom-topic-submit"
              >
                Create topic
              </Button>
            </Group>
          </Stack>
        )}

        {/* MED-851 AC6/AC7: explicit Save / Cancel footer. Save commits the
            staged topic + display_name; Cancel discards and closes. Outside
            click and Escape also discard (Mantine Modal default onClose). */}
        <Group
          justify="flex-end"
          gap="sm"
          pt="md"
          style={{
            borderTop: '1px solid var(--mantine-color-gray-2)',
          }}
        >
          <Button variant="subtle" color="gray" onClick={onClose} data-testid="topic-modal-cancel">
            Cancel
          </Button>
          <Button
            color="medleyGreen"
            onClick={handleSave}
            disabled={!canSave}
            data-testid="topic-modal-save"
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// Individual topic row component
type TopicRowPill = 'current' | 'selected' | 'none';

interface TopicRowProps {
  topic: Topic;
  pill: TopicRowPill;
  onClick: () => void;
}

function TopicRow({ topic, pill, onClick }: TopicRowProps) {
  return (
    <UnstyledButton
      onClick={onClick}
      py="sm"
      px="sm"
      style={(theme) => ({
        borderRadius: theme.radius.sm,
        '&:hover': {
          backgroundColor: theme.colors.gray[0],
        },
      })}
      styles={{
        root: {
          '&:hover': {
            backgroundColor: 'var(--mantine-color-gray-0)',
          },
        },
      }}
      data-testid={
        pill === 'selected'
          ? 'topic-row-selected'
          : pill === 'current'
            ? 'topic-row-current'
            : undefined
      }
    >
      <Group justify="space-between">
        <Text size="sm">{topic.name}</Text>
        {pill === 'current' && (
          <Badge size="sm" variant="light" color="gray" leftSection={<Check size={10} />}>
            Current
          </Badge>
        )}
        {pill === 'selected' && (
          <Badge size="sm" variant="light" color="medleyGreen" leftSection={<Check size={10} />}>
            Selected
          </Badge>
        )}
      </Group>
    </UnstyledButton>
  );
}

export default TopicSelectModal;
